import { Button, cn, Input, panelCard } from "@pax/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { authClient } from "@/lib/auth-client";
import { checkSession, checksSessions } from "@/lib/risk";

type Outcome = { kind: "approved" } | { kind: "denied" } | { kind: "failed"; reason: string };

/**
 * Where someone who manages agents or evals confirms a sign-in the lab CLI started with
 * `bun lab login`: the code it shows in the terminal arrives in the link, or is typed in here.
 */
export const Route = createFileRoute("/_authenticated/device")({
  staticData: { access: { anyOf: ["agents.manage"] } },
  validateSearch: (search: Record<string, unknown>): { user_code?: string } => ({
    user_code: typeof search.user_code === "string" ? search.user_code : undefined,
  }),
  head: () => ({ meta: [{ title: "Sign in the lab · Pax.co" }] }),
  component: DevicePage,
});

/**
 * Approves the code. A browser whose sign-in check is 30 days old is refused, so it's checked again
 * and the approval retried once.
 */
async function approveChecked(userCode: string) {
  const first = await authClient.device.approve({ userCode });
  if (first.error?.status !== 403 || !(await checksSessions())) return first;
  try {
    await checkSession();
  } catch {
    return first;
  }
  return authClient.device.approve({ userCode });
}

function DevicePage() {
  const { user_code } = Route.useSearch();
  const { session } = Route.useRouteContext();
  const [code, setCode] = useState(user_code ?? "");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState(false);

  /** Better Auth only lets the session that looked a code up answer it, so the lookup comes first. */
  const answer = async (approve: boolean) => {
    setBusy(true);
    const userCode = code.trim().toUpperCase();
    const claimed = await authClient.device({ query: { user_code: userCode } });
    const result = claimed.error
      ? claimed
      : approve
        ? await approveChecked(userCode)
        : await authClient.device.deny({ userCode });
    setBusy(false);
    setOutcome(
      result.error
        ? { kind: "failed", reason: failureReason(result.error) }
        : { kind: approve ? "approved" : "denied" },
    );
  };

  return (
    <main className="grid min-h-dvh place-items-center p-6">
      <div className={cn("flex w-full max-w-sm flex-col gap-4 p-6", panelCard)}>
        <div className="flex flex-col gap-1">
          <h1 className="text-lg font-medium">Sign in the lab</h1>
          <p className="text-sm text-pretty text-muted-foreground">
            The lab will act as {session.user.email}: what it publishes and runs is yours, and its
            eval games spend your credits.
          </p>
        </div>
        {outcome === null ? (
          <>
            <Input
              value={code}
              onChange={event => setCode(event.target.value)}
              placeholder="ABCD-EFGH"
              aria-label="The code in your terminal"
              className="text-center font-mono tracking-widest uppercase"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                disabled={busy || !code.trim()}
                onClick={() => void answer(false)}
              >
                Deny
              </Button>
              <Button
                className="flex-1"
                disabled={busy || !code.trim()}
                onClick={() => void answer(true)}
              >
                Approve
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm">
            {outcome.kind === "approved"
              ? "Done. You can close this tab and go back to your terminal."
              : outcome.kind === "denied"
                ? "Denied. The lab was not signed in."
                : `${outcome.reason} Run \`bun lab login\` again.`}
          </p>
        )}
      </div>
    </main>
  );
}

function failureReason(error: { message?: string; error_description?: unknown }): string {
  const description =
    typeof error.error_description === "string" ? error.error_description : error.message;
  return description ? `${description}.` : "The sign-in failed.";
}
